import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLoginModal } from '../../context/LoginModalContext';
import { useSettings } from '../../context/SettingsContext';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import useAuthorAvatar from '../../utils/useAuthorAvatar';
import './RightSidebar.css';
import notificationsApi from '../../services/notifications';

const AuthAccountItem = ({ username, accountType, profileImage, onSwitch }) => {
  const { avatarSources, handleAvatarError } = useAuthorAvatar({ author: username, profileImage });
  return (
    <button className="auth-account-item" onClick={onSwitch}>
      <img
        src={avatarSources[0] || ''}
        alt={username}
        className="auth-account-avatar"
        data-avatar-fallback-index="0"
        onError={handleAvatarError}
      />
      <span>{accountType === 'keychain' ? '🔑' : '🔒'} @{username}</span>
    </button>
  );
};

const RightSidebar = ({ onClose }) => {
  const { user, logout, getSavedAccounts, loginWithSavedAccount, loginWithPostingKey, checkPromptUpdateStoredKey } = useAuth();
  const { openLoginModal } = useLoginModal();
  const { notiflexionsEnabled } = useSettings();
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [quickLoginPin, setQuickLoginPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingKey, setIsUpdatingKey] = useState(false);
  const [newPostingKeyInput, setNewPostingKeyInput] = useState('');
  const [notiflexionCount, setNotiflexionCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [manaData, setManaData] = useState({ voting: 100, rc: 100 });
  const [isRefreshingMana, setIsRefreshingMana] = useState(false);

  useBodyScrollLock(showPinModal);
  useBodyScrollLock(expanded);

  const fetchMana = async (username) => {
    try {
      const { Client } = require('@hiveio/dhive');
      const client = new Client(['https://api.hive.blog']);
      const accounts = await client.database.getAccounts([username]);
      const rcAccounts = await client.rc.findRCAccounts([username]);
      
      if (!accounts.length || !rcAccounts.length) return;
      
      const account = accounts[0];
      const rcAccount = rcAccounts[0];
      
      const calcMana = (current, lastUpdate, max) => {
        if (!max || max <= 0) return 100;
        const elapsed = (Date.now() / 1000) - lastUpdate;
        const recovered = (elapsed * max) / 432000; // 5 days
        let mana = ((parseFloat(current) + recovered) / max) * 100;
        return Math.min(Math.max(mana, 0), 100);
      };

      const maxVotingMana = parseFloat(account.post_voting_power?.split(' ')[0] || 0) * 1000000;
      const votingMana = calcMana(account.voting_manabar.current_mana, account.voting_manabar.last_update_time, maxVotingMana);
      const rcMana = calcMana(rcAccount.rc_manabar.current_mana, rcAccount.rc_manabar.last_update_time, parseFloat(rcAccount.max_rc));
      
      setManaData({ voting: votingMana.toFixed(2), rc: rcMana.toFixed(2) });
    } catch (err) {
      console.error('Error fetching mana:', err);
    }
  };

  useEffect(() => {
    if (!user?.username) return;
    fetchMana(user.username);
  }, [user?.username]);

  const handleRefreshMana = async (e) => {
    e.stopPropagation();
    if (!user?.username || isRefreshingMana) return;
    setIsRefreshingMana(true);
    await fetchMana(user.username);
    setIsRefreshingMana(false);
  };

  useEffect(() => {
    if (!user?.username) return;

    fetchNotiflexionCount(user.username);
    const intervalId = setInterval(() => {
      fetchNotiflexionCount(user.username);
    }, 15000);

    return () => clearInterval(intervalId);
  }, [user?.username]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-profile-menu-open', showProfileMenu ? 'true' : 'false');
    return () => {
      root.removeAttribute('data-profile-menu-open');
    };
  }, [showProfileMenu]);

  const fetchNotiflexionCount = async (username) => {
    try {
      const count = await notificationsApi.unreadCount(username);
      setNotiflexionCount(Number(count) || 0);
    } catch (error) {
      console.error('Error fetching notiflexion count:', error);
      setNotiflexionCount(0);
    }
  };

  const handleAccountSwitch = async (username, accountType) => {
    if (accountType === 'keychain') {
      setPinError('');
      setIsLoading(true);
      try {
        await loginWithSavedAccount(username);
      } catch (err) {
        setPinError(err.message || 'Keychain login failed');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    setSelectedAccount(username);
    setShowPinModal(true);
    setQuickLoginPin('');
    setIsUpdatingKey(false);
    setNewPostingKeyInput('');
    setPinError('');
  };

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    setPinError('');

    if (quickLoginPin.length !== 6) {
      setPinError('PIN must be 6 digits');
      return;
    }

    setIsLoading(true);
    try {
      if (isUpdatingKey) {
        if (!newPostingKeyInput.trim()) {
          setPinError('Please enter your new posting key');
          setIsLoading(false);
          return;
        }
        await loginWithPostingKey(selectedAccount, newPostingKeyInput.trim(), quickLoginPin);
        setShowPinModal(false);
        if (checkPromptUpdateStoredKey) {
          checkPromptUpdateStoredKey(selectedAccount, newPostingKeyInput.trim());
        }
      } else {
        await loginWithSavedAccount(selectedAccount, quickLoginPin);
        setShowPinModal(false);
      }
    } catch (err) {
      if (err.code === 'KEY_OUTDATED') {
        setIsUpdatingKey(true);
        setPinError('Your stored posting key is no longer valid or changed on Hive. Please enter your new posting key below.');
      } else {
        setPinError(err.message || 'Invalid PIN');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const savedAccounts = getSavedAccounts();
  const otherAccounts = Object.keys(savedAccounts).filter(username => username !== user?.username);
  const { avatarSources: userAvatarSources, handleAvatarError: handleUserAvatarError } = useAuthorAvatar({
    author: user?.username,
    profileImage: user?.profile?.profileImage,
  });
  const firstOtherAccount = otherAccounts[0] || null;
  const { avatarSources: firstOtherAvatarSources, handleAvatarError: handleFirstOtherAvatarError } = useAuthorAvatar({
    author: firstOtherAccount,
    profileImage: firstOtherAccount ? (savedAccounts[firstOtherAccount]?.profileImage || '') : '',
  });

  return (
    <>
      <aside className={`right-sidebar${expanded ? ' expanded' : ''}`}>
        <div className="sidebar-inner">
        <div className="sidebar-header">
          {/* Mobile close button - shown only at <= 768px */}
          <button
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          {/* Expand/collapse toggle - shown at 769px - 1024px */}
          <button
            className="sidebar-toggle-btn"
            onClick={() => setExpanded(v => !v)}
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {expanded ? (
              /* Collapse: two right-pointing chevrons */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="13 17 18 12 13 7" />
                <polyline points="6 17 11 12 6 7" />
              </svg>
            ) : (
              /* Expand: two left-pointing chevrons */
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            )}
          </button>
        </div>
        <div className="right-sidebar-content">
          {/* Notifications Link */}
          {user && (
            <Link to="/notifications" className="notiflexion-link">
              <div className="notiflexion-header">
                <svg className="notiflexion-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                <span>{notiflexionsEnabled ? 'Notiflexions' : 'Notifications'}</span>
              </div>
              {notiflexionCount > 0 && (
                <span className="notiflexion-count">{notiflexionCount}</span>
              )}
            </Link>
          )}

          {/* Auth Section */}
          {user ? (
            <div className="sidebar-auth">
              <div 
                className="auth-user-info"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              >
                <div className="auth-user-header-content">
                  <div className="auth-user-header-left">
                    <div className="auth-avatar-container">
                      <img
                        src={userAvatarSources[0] || ''}
                        alt={user.username}
                        className="auth-avatar"
                        data-avatar-fallback-index="0"
                        onError={handleUserAvatarError}
                      />
                      <svg 
                        className={`profile-menu-chevron ${showProfileMenu ? 'open' : ''}`}
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </div>
                    <div className="auth-user-details">
                      <Link to={`/user/${user.username}`} className="auth-username" onClick={(e) => e.stopPropagation()}>
                        @{user.username}
                      </Link>
                      <Link to="/settings" className="auth-settings-link" onClick={(e) => e.stopPropagation()}>Settings</Link>
                    </div>
                  </div>
                </div>
                <div className="auth-user-mana-container">
                  <div className="auth-user-mana-bars">
                    <div className="mana-progress-wrapper" title={`Voting mana: ${manaData?.voting || 0}%`}>
                      <div className={`mana-progress mana-micro ${!manaData ? 'loading' : ''}`}>
                        <div className="mana-progress-bar bg-primary" style={{ width: `${manaData?.voting || 0}%` }}></div>
                      </div>
                      <div className="mana-value-text">Voting: {manaData?.voting || '...'}%</div>
                    </div>
                    <div className="mana-progress-wrapper" title={`Resource credits: ${manaData?.rc || 0}%`}>
                      <div className={`mana-progress mana-micro ${!manaData ? 'loading' : ''}`}>
                        <div className="mana-progress-bar bg-rc" style={{ width: `${manaData?.rc || 0}%` }}></div>
                      </div>
                      <div className="mana-value-text">RC: {manaData?.rc || '...'}%</div>
                    </div>
                  </div>
                  <button 
                    className={`mana-refresh-btn ${isRefreshingMana ? 'refreshing' : ''}`} 
                    onClick={handleRefreshMana}
                    title="Refresh stats"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Profile Action Icons - visible between 768px-1024px on click */}
              {showProfileMenu && (
                <div className="profile-menu">
                  <Link 
                    to="/settings" 
                    className="profile-menu-item"
                    title="Settings"
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <svg className="setting-svg" viewBox="0 0 512 512" fill="none" stroke="currentColor" strokeWidth="2">
                      <path fill="currentColor" d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"></path>
                    </svg>
                  </Link>
                  {otherAccounts.length > 0 && (
                    <button 
                      className="profile-menu-item"
                      title="Switch Account"
                      onClick={() => {
                        if (otherAccounts.length > 0) {
                          handleAccountSwitch(otherAccounts[0], savedAccounts[otherAccounts[0]].type || 'postingkey');
                        }
                      }}
                    >
                      <img
                        src={firstOtherAvatarSources[0] || ''}
                        alt={firstOtherAccount}
                        className="auth-account-avatar"
                        data-avatar-fallback-index="0"
                        onError={handleFirstOtherAvatarError}
                      />
                    </button>
                  )}
                  <button 
                    className="profile-menu-item"
                    onClick={openLoginModal}
                    title="Add Account"
                  >
                    <svg className="add-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '-2px' }}>
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <svg className="add-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '-2px' }}>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                  </button>
                  
                  <button 
                    className="profile-menu-item logout-btn"
                    onClick={() => {
                      logout();
                      setShowProfileMenu(false);
                    }}
                    title="Logout"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                </div>
              )}

              {otherAccounts.length > 0 && (
                <div className="auth-accounts">
                  <div className="auth-accounts-header">Switch Account</div>
                  {otherAccounts.slice(0, 3).map(username => {
                    const account = savedAccounts[username];
                    return (
                      <AuthAccountItem
                        key={username}
                        username={username}
                        accountType={account.type || 'postingkey'}
                        profileImage={account.profileImage}
                        onSwitch={() => handleAccountSwitch(username, account.type || 'postingkey')}
                      />
                    );
                  })}
                </div>
              )}

              <button
                className="auth-add-account"
                onClick={openLoginModal}
              >
                <svg className="add-account-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Account
              </button>

              <button onClick={logout} className="auth-logout">
                Logout
              </button>
            </div>
          ) : (
            <button 
              className="sidebar-login-btn"
              onClick={openLoginModal} title="Login"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              <span>Login</span>
            </button>
          )}
        </div>
        </div>
      </aside>

      {expanded && createPortal(
        <div className="sidebar-expanded-backdrop" onClick={() => setExpanded(false)} />,
        document.body
      )}

      {showPinModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowPinModal(false)}>
          <div className="pin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter PIN for @{selectedAccount}</h3>
              <button className="close-btn" onClick={() => setShowPinModal(false)}>×</button>
            </div>
            <form onSubmit={handleQuickLogin}>
              {pinError && <div className="error-message">{pinError}</div>}
              {isUpdatingKey && (
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    New Hive Posting Key
                  </label>
                  <input
                    type="password"
                    className="pin-input"
                    value={newPostingKeyInput}
                    onChange={(e) => setNewPostingKeyInput(e.target.value)}
                    placeholder="5K... (New posting key)"
                    autoFocus
                    disabled={isLoading}
                    style={{ fontSize: '0.9rem', letterSpacing: 'normal' }}
                  />
                </div>
              )}
              <div className="form-group">
                {isUpdatingKey && (
                  <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '6px', color: 'var(--text-secondary)' }}>
                    Enter 6-digit PIN to secure key
                  </label>
                )}
                <input
                  type="password"
                  inputMode="numeric"
                  className="pin-input"
                  value={quickLoginPin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setQuickLoginPin(value);
                  }}
                  placeholder="••••••"
                  maxLength={6}
                  autoFocus={!isUpdatingKey}
                  disabled={isLoading}
                />
              </div>
              <button 
                type="submit"
                className="login-btn"
                disabled={isLoading || quickLoginPin.length !== 6 || (isUpdatingKey && !newPostingKeyInput.trim())}
              >
                {isLoading ? (isUpdatingKey ? 'Updating & Logging in...' : 'Logging in...') : (isUpdatingKey ? 'Update Key & Login' : 'Login')}
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                  onClick={() => {
                    setIsUpdatingKey(!isUpdatingKey);
                    setPinError('');
                  }}
                >
                  {isUpdatingKey ? 'Cancel key update' : 'Posting key changed? Update it here'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default RightSidebar;
